import type { Assessment } from "../assessment-types";

/** 🧠 Thoughts & Protective Patterns — phase 1 expansion. */

export const OVERTHINKING_ASSESSMENT: Assessment = {
  slug: "overthinking-rumination",
  emoji: "🌀",
  title: "Overthinking & Rumination",
  tagline: "When your mind struggles to let something go",
  category: "thoughts",
  keywords: ["overthinking", "rumination", "racing thoughts", "replaying", "analysis", "worry loops"],
  intro:
    "This reflects on how often your thoughts circle back over the same ground. Overthinking is often the mind's way of trying to keep you safe.",
  questions: [
    { id: "q1", text: "Do you replay conversations after they've finished?" },
    { id: "q2", text: "Do you analyse what someone meant by a message or a look?" },
    { id: "q3", text: "Do your thoughts speed up when you lie down at night?" },
    { id: "q4", text: "Do you find it hard to make a decision because you keep weighing it up?" },
    { id: "q5", text: "Do you search for the 'right' answer even when there isn't one?" },
    { id: "q6", text: "Do you imagine everything that could go wrong in advance?" },
    { id: "q7", text: "Do you feel mentally tired from thinking?" },
    { id: "q8", text: "Do you struggle to be present because your mind is elsewhere?" },
    { id: "q9", text: "Do you go over past mistakes long after they happened?" },
    { id: "q10", text: "Do you seek certainty before you can settle?" },
    { id: "q11", text: "Do you find it hard to stop once the thinking starts?" },
    { id: "q12", text: "Would you like more ways to let a thought rest?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest your thinking usually settles without too much circling.",
      body:
        "Some rehearsing and reviewing is normal. Knowing what helps your mind rest keeps that steadiness available.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest thoughts sometimes circle for longer than you'd like.",
      body:
        "Rumination often tries to prevent something painful from happening again. Bringing attention into the body can interrupt the loop more effectively than arguing with it.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest overthinking may take up a lot of your energy.",
      body:
        "This pattern usually develops where being prepared felt necessary. Gentle interruption, movement and writing things down often help more than trying to stop thinking.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest your mind may rarely get a rest from analysing.",
      body:
        "That is genuinely tiring. Small, repeated practices that bring you back into the present tend to help, and support from someone appropriately qualified can be worth considering.",
    },
  ],
  explore: {
    understand: [
      { label: "Hypervigilance explained", to: "/learn/hypervigilance" },
      { label: "Trauma glossary", to: "/learn/glossary" },
    ],
    try: [
      { label: "CBT thought cards", to: "/cbt-thought-cards" },
      { label: "Breathing exercises", to: "/breathing" },
    ],
    reflect: [
      { label: "Guided journal", to: "/guided-journal" },
      { label: "Daily check-in", to: "/check-in" },
    ],
  },
};

export const INNER_CRITIC_ASSESSMENT: Assessment = {
  slug: "inner-critic",
  emoji: "🗣️",
  title: "Inner Critic",
  tagline: "How harshly do you speak to yourself?",
  category: "thoughts",
  keywords: ["inner critic", "self-criticism", "harsh", "perfectionism", "shame", "self-talk"],
  intro:
    "This reflects on the tone of your inner voice. The critic usually formed to protect you from criticism elsewhere — understanding it is more useful than fighting it.",
  questions: [
    { id: "q1", text: "Do you notice a harsh voice commenting on what you do?" },
    { id: "q2", text: "Do you call yourself names you'd never use about someone else?" },
    { id: "q3", text: "Do you focus on the one thing that went wrong rather than what went well?" },
    { id: "q4", text: "Do you feel you should be doing better than you are?" },
    { id: "q5", text: "Do you anticipate criticism before it arrives?" },
    { id: "q6", text: "Do you dismiss compliments or good feedback?" },
    { id: "q7", text: "Do you hold yourself to standards you'd consider unfair for others?" },
    { id: "q8", text: "Do you feel ashamed after ordinary mistakes?" },
    { id: "q9", text: "Do you hesitate to try things in case you fail?" },
    { id: "q10", text: "Does the critical voice sound like someone from your past?" },
    { id: "q11", text: "Do you find it hard to feel proud of what you've done?" },
    { id: "q12", text: "Would you like your inner voice to feel less punishing?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest your inner voice is reasonably fair with you at the moment.",
      body:
        "Most people have a critical voice somewhere. Noticing when it gets louder can tell you a lot about what feels at stake.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest a critical inner voice shows up some of the time.",
      body:
        "The inner critic often learned that being hard on you would keep you safe from other people's judgement. Curiosity works better than arguing with it.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest self-criticism may be a frequent presence.",
      body:
        "This pattern can quietly shape decisions, rest and relationships. Separating the voice from your own view of yourself is often a useful first move.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest the inner critic may be very loud for you right now.",
      body:
        "Living with that is painful and it isn't a reflection of your worth. Support from someone appropriately qualified can help, especially where shame runs deep.",
    },
  ],
  explore: {
    understand: [
      { label: "The inner critic", to: "/learn/inner-critic" },
      { label: "Shame-based trauma", to: "/learn/shame-based-trauma" },
    ],
    try: [
      { label: "EFT tapping", to: "/tapping" },
      { label: "Affirmations", to: "/affirmations" },
    ],
    reflect: [
      { label: "Your inner team", to: "/integrate/your-inner-team" },
      { label: "Shadow work", to: "/shadow-work" },
    ],
  },
};

export const AVOIDANCE_ASSESSMENT: Assessment = {
  slug: "avoidance-patterns",
  emoji: "🚫",
  title: "Avoidance Patterns",
  tagline: "What do you avoid to feel safer or reduce distress?",
  category: "thoughts",
  keywords: ["avoidance", "procrastination", "escape", "distraction", "putting off", "numbing"],
  intro:
    "This reflects on what you tend to step around. Avoidance usually brings relief in the short term, which is exactly why it becomes a pattern.",
  questions: [
    { id: "q1", text: "Do you put off tasks that bring up difficult feelings?" },
    { id: "q2", text: "Do you avoid places or people that remind you of hard experiences?" },
    { id: "q3", text: "Do you stay busy to keep certain thoughts at a distance?" },
    { id: "q4", text: "Do you delay conversations you know you need to have?" },
    { id: "q5", text: "Do you distract yourself when a feeling starts to rise?" },
    { id: "q6", text: "Do you decline invitations more than you'd like to?" },
    { id: "q7", text: "Do you feel relief when something is cancelled?" },
    { id: "q8", text: "Do you avoid thinking about the future?" },
    { id: "q9", text: "Do you notice your world getting a little smaller over time?" },
    { id: "q10", text: "Do you avoid appointments related to your health or wellbeing?" },
    { id: "q11", text: "Do you feel guilty about what you've been avoiding?" },
    { id: "q12", text: "Would you like to approach some of these things in small steps?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest avoidance isn't shaping much of your life right now.",
      body:
        "Stepping back from something difficult sometimes is healthy. Noticing the difference between rest and avoidance is a useful skill.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest some avoidance patterns may be present.",
      body:
        "Avoidance works — that's why it sticks. Approaching one small thing at a time, with support, usually feels more possible than facing everything at once.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest avoidance may be doing a lot of work for you.",
      body:
        "Over time this can quietly narrow your world. Very gradual, chosen steps — at your pace — tend to widen it again without overwhelming your system.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest avoidance may be significantly shaping your daily life.",
      body:
        "That is understandable when facing things has felt unsafe. Working with an appropriately qualified professional can make graded steps feel safer and more manageable.",
    },
  ],
  explore: {
    understand: [
      { label: "Trauma responses explained", to: "/learn/trauma-responses" },
      { label: "Therapies and healing approaches", to: "/learn/therapies" },
    ],
    try: [
      { label: "Quick nervous-system resets", to: "/quick-resets" },
      { label: "Grounding exercises", to: "/grounding" },
    ],
    reflect: [
      { label: "Guided journal", to: "/guided-journal" },
      { label: "Micro-adventures", to: "/integrate/micro-adventures" },
    ],
  },
};

export const HYPER_INDEPENDENCE_ASSESSMENT: Assessment = {
  slug: "hyper-independence",
  emoji: "🧱",
  title: "Hyper-Independence",
  tagline: "When relying on yourself feels safer than relying on others",
  category: "thoughts",
  keywords: ["independence", "asking for help", "self-reliance", "control", "avoidant", "doing it alone"],
  intro:
    "This reflects on how it feels to depend on other people. Doing everything yourself is often a strategy that once made sense, rather than a preference.",
  questions: [
    { id: "q1", text: "Do you find it hard to ask for help even when you need it?" },
    { id: "q2", text: "Do you assume things will only be done properly if you do them?" },
    { id: "q3", text: "Do you feel uncomfortable being looked after?" },
    { id: "q4", text: "Do you keep difficulties to yourself?" },
    { id: "q5", text: "Do you feel safer when you don't have to rely on anyone?" },
    { id: "q6", text: "Do you take on more than your share?" },
    { id: "q7", text: "Do you find it hard to delegate?" },
    { id: "q8", text: "Do you feel exposed when someone sees you struggling?" },
    { id: "q9", text: "Do you decline offers of support automatically?" },
    { id: "q10", text: "Do you feel you'd be a burden if you leaned on someone?" },
    { id: "q11", text: "Do you get to the point of exhaustion before you say anything?" },
    { id: "q12", text: "Would you like receiving support to feel less uncomfortable?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest you can generally accept support when you need it.",
      body:
        "Being able to move between independence and connection is a real strength worth protecting.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest relying on others is sometimes uncomfortable for you.",
      body:
        "Self-reliance often develops where support wasn't dependable. Letting someone help with something small is a low-risk way to test the water.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest hyper-independence may be a familiar pattern.",
      body:
        "It is protective and it is also heavy. Receiving support may feel unfamiliar rather than unwanted, and that usually eases with practice.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest you may be carrying almost everything alone.",
      body:
        "That takes remarkable strength and it also costs a lot. Even one trusted person, or a supportive space, can begin to change what depending on others feels like.",
    },
  ],
  explore: {
    understand: [
      { label: "Attachment styles", to: "/learn/attachment-styles" },
      { label: "Complex trauma", to: "/learn/cptsd" },
    ],
    try: [
      { label: "Acts of self-care", to: "/acts-of-self-care" },
      { label: "Community space", to: "/community" },
    ],
    reflect: [
      { label: "Self-leadership", to: "/integrate/self-leadership" },
      { label: "Guided journal", to: "/guided-journal" },
    ],
  },
};
